// L'objet NOMMÉ par la voix s'éclaire (lecteur 3). Pur.
//
// Un objet déclare ses noms (`mentions` : « AB », « hypoténuse ») ; quand
// une phrase de la narration les contient, le lecteur l'éclaire (halo doré)
// à l'instant estimé où la voix dit le mot : début de la phrase sur
// l'horloge de la scène + (position du mot dans la phrase) × (durée parlée de
// la phrase). L'action `object.highlight` éclaire de la même façon quand le
// nom n'est pas dit (« ce côté-là »).
//
// Recherche dans le TEXTE affiché (`narration.text`, formules comprises :
// « AB » est trouvé dans « $AB^2 + AC^2$ ») :
//   - un nom en majuscules (« AB », « A ») : tel quel, en mot entier
//     (« AB » n'est pas trouvé dans « ABC ») ; un segment dans les deux sens
//     (« AB » trouve « BA ») ;
//   - un nom avec des minuscules (« hypoténuse ») : sans tenir compte des
//     majuscules ni des accents, en mot entier (« Hypoténuse » compte).

import { splitSentences, SENTENCE_PAUSE_MS } from './narration';
import type { JrafScene } from './schema/document';
import type { JrafObject } from './schema/objects';

/** Durée d'un éclairage déclenché par un nom (ms). */
export const MENTION_GLOW_MS = 1800;
/** L'éclairage commence un peu avant le mot (la voix et l'estimation ne sont pas au mot près). */
export const MENTION_LEAD_MS = 150;
/** Montée et descente de l'éclairage (ms). */
export const GLOW_RISE_MS = 250;
export const GLOW_FALL_MS = 450;

export interface Highlight {
  objectId: string;
  start: number;
  end: number;
  /** nommé par la voix, ou action `object.highlight` */
  source: 'mention' | 'step';
}

/**
 * Phrase « à chercher », de MÊME longueur que la phrase (les positions
 * restent celles du texte) : délimiteurs `$`, commandes TeX et accolades
 * remplacés par des espaces ; `math[i]` dit si le caractère i est dans une
 * formule.
 */
function searchable(sentence: string): { text: string; math: boolean[] } {
  const chars = [...sentence];
  const math: boolean[] = [];
  let inMath = false;
  for (let i = 0; i < chars.length; i++) {
    const c = chars[i]!;
    if (c === '$' && chars[i - 1] !== '\\') {
      inMath = !inMath;
      chars[i] = ' ';
      math.push(false);
      continue;
    }
    math.push(inMath);
    if (inMath && (c === '{' || c === '}')) chars[i] = ' ';
  }
  let text = chars.join('');
  text = text.replace(/\\[a-zA-Z]+/g, (m) => ' '.repeat(m.length));
  return { text, math };
}

/** Sans accents ni majuscules, caractère par caractère (même longueur). */
function foldChars(s: string): string {
  return [...s].map((c) => (c.normalize('NFD')[0] ?? c).toLowerCase()).join('');
}

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Ni lettre ni chiffre avant un nom (« l’hypoténuse » compte) ; ni apostrophe après (« C’est », « $A’$ »). */
const BEFORE = '(?<![\\p{L}\\p{N}])';
const AFTER = "(?![\\p{L}\\p{N}'\u2019\u2032])";

/**
 * Positions (0 … 1, part de la phrase avant le nom) des occurrences des noms
 * dans une phrase, triées. Un nom d'une seule lettre (« A », « a ») n'est
 * cherché que dans les formules : « $A$ », pas l'article « à », le verbe
 * « a » ni « C’est ».
 */
export function mentionPositions(sentence: string, mentions: readonly string[]): number[] {
  const { text, math } = searchable(sentence);
  const len = [...sentence].length;
  if (!text.trim() || len === 0) return [];
  const folded = foldChars(text);
  const out: number[] = [];
  for (const raw of mentions) {
    const name = raw.trim().replace(/\s+/g, ' ');
    if (!name) continue;
    const caseless = /\p{Ll}/u.test(name) && name.length > 1;
    const single = [...name].length === 1;
    // un segment se dit dans les deux sens : [AB] = [BA]
    const names = /^[A-Z]{2}$/.test(name) ? [name, `${name[1]}${name[0]}`] : [name];
    const alt = names.map((n) => escapeRe(caseless ? foldChars(n) : n)).join('|');
    const re = new RegExp(`${BEFORE}(?:${alt})${AFTER}`, 'gu');
    const hay = caseless ? folded : text;
    for (const m of hay.matchAll(re)) {
      // index en points de code (les positions de `math` aussi)
      const at = [...hay.slice(0, m.index ?? 0)].length;
      if (single && !math[at]) continue;
      out.push(at / len);
    }
  }
  return out.sort((a, b) => a - b);
}

/**
 * Éclairages déclenchés par les noms dits dans une scène. `sentenceStartsMs` :
 * débuts des phrases sur l'horloge de la scène (étirés) ; `voiceStartsMs` et
 * `voiceTotalMs` : débuts et durée dans la voix (durée parlée de chaque phrase).
 */
export function mentionHighlights(
  scene: Pick<JrafScene, 'narration'>,
  objects: readonly Pick<JrafObject, 'id' | 'mentions'>[],
  sentenceStartsMs: readonly number[],
  voiceStartsMs: readonly number[],
  voiceTotalMs: number,
): Highlight[] {
  const named = objects.filter((o) => o.mentions?.length);
  if (named.length === 0) return [];
  // phrases dites autrement (« A B » pour « $AB$ ») : même découpage (validé),
  // on cherche les noms dans le texte affiché, au prorata de la phrase dite
  const shown = splitSentences(scene.narration.text);
  const out: Highlight[] = [];
  shown.forEach((sentence, i) => {
    const start = sentenceStartsMs[i];
    const voiceStart = voiceStartsMs[i];
    if (start === undefined || voiceStart === undefined) return;
    const next = voiceStartsMs[i + 1];
    const speech = Math.max(
      0,
      (next ?? voiceTotalMs) - voiceStart - (next !== undefined ? SENTENCE_PAUSE_MS : 0),
    );
    for (const o of named) {
      for (const p of mentionPositions(sentence, o.mentions!)) {
        const at = start + p * speech;
        out.push({
          objectId: o.id,
          start: Math.max(start, Math.round(at - MENTION_LEAD_MS)),
          end: Math.round(at + MENTION_GLOW_MS),
          source: 'mention',
        });
      }
    }
  });
  return mergeHighlights(out);
}

/** Fusionne les éclairages qui se chevauchent sur un même objet ; triés par début. */
export function mergeHighlights(list: readonly Highlight[]): Highlight[] {
  const sorted = [...list].sort(
    (a, b) => a.objectId.localeCompare(b.objectId) || a.start - b.start,
  );
  const out: Highlight[] = [];
  for (const h of sorted) {
    const last = out[out.length - 1];
    if (last && last.objectId === h.objectId && h.start <= last.end + GLOW_FALL_MS) {
      last.end = Math.max(last.end, h.end);
      if (h.source === 'step') last.source = 'step';
    } else out.push({ ...h });
  }
  return out.sort((a, b) => a.start - b.start || a.objectId.localeCompare(b.objectId));
}

/** Intensité d'un éclairage à l'instant t (0 … 1) ; mouvement réduit : tout ou rien. */
export function glowAt(
  h: Pick<Highlight, 'start' | 'end'>,
  t: number,
  reducedMotion = false,
): number {
  if (t < h.start) return 0;
  if (reducedMotion) return t < h.end ? 1 : 0;
  if (t < h.start + GLOW_RISE_MS) return (t - h.start) / GLOW_RISE_MS;
  if (t < h.end) return 1;
  if (t < h.end + GLOW_FALL_MS) return 1 - (t - h.end) / GLOW_FALL_MS;
  return 0;
}

// ------------------------------------------------------------ contrôles

export interface MentionIssue {
  code: 'mention_missing' | 'mention_unused';
  /** objet en cause (`mention_unused`) */
  objectIndex?: number;
  /** scène où le nom est dit (`mention_missing`) */
  sceneIndex?: number;
  message: string;
}

/** Noms de segments (« AB ») et de figures (« ABC ») dits dans une phrase. */
function namedSegments(sentence: string): string[] {
  const out: string[] = [];
  const re = new RegExp(`${BEFORE}([A-Z]{2})${AFTER}`, 'gu');
  for (const m of searchable(sentence).text.matchAll(re)) out.push(m[1]!);
  return out;
}

/**
 * - `mention_missing` (qualité) : la voix nomme un segment entre deux points
 *   étiquetés de la figure (« AB ») et aucun objet ne le déclare dans ses
 *   `mentions` : il ne s'éclairera pas — il faut un objet par côté nommé ;
 * - `mention_unused` (conseil) : un nom déclaré n'est jamais dit.
 */
export function checkMentions(doc: {
  objects: readonly JrafObject[];
  scenes: readonly Pick<JrafScene, 'title' | 'narration'>[];
}): MentionIssue[] {
  const out: MentionIssue[] = [];
  const labels = new Set(
    doc.objects
      .filter((o): o is Extract<JrafObject, { type: 'point' }> => o.type === 'point')
      .map((o) => o.label?.trim() ?? '')
      .filter((l) => /^[A-Z]$/.test(l)),
  );
  const declared = new Set(
    doc.objects.flatMap((o) => (o.mentions ?? []).map((m) => m.trim().replace(/[[\]\s]/g, ''))),
  );
  const reported = new Set<string>();
  doc.scenes.forEach((scene, si) => {
    for (const sentence of splitSentences(scene.narration.text)) {
      for (const seg of namedSegments(sentence)) {
        const [a, b] = [seg[0]!, seg[1]!];
        if (a === b || !labels.has(a) || !labels.has(b)) continue;
        const rev = `${b}${a}`;
        if (declared.has(seg) || declared.has(rev) || reported.has(seg) || reported.has(rev))
          continue;
        reported.add(seg);
        out.push({
          code: 'mention_missing',
          sceneIndex: si,
          message: `« ${seg} » est nommé par la voix (scène « ${scene.title} ») mais aucun objet ne le désigne : fais du côté [${seg}] un objet à part (line) avec mentions ["${seg}"], pour qu'il s'éclaire quand la voix le dit`,
        });
      }
    }
  });
  const sentences = doc.scenes.flatMap((s) => splitSentences(s.narration.text));
  doc.objects.forEach((o, oi) => {
    for (const name of o.mentions ?? []) {
      if (sentences.some((s) => mentionPositions(s, [name]).length > 0)) continue;
      out.push({
        code: 'mention_unused',
        objectIndex: oi,
        message: `« ${name} » (noms de « ${o.id} ») n'est jamais dit dans la narration : « ${o.id} » ne s'éclairera pas sur ce nom`,
      });
    }
  });
  return out;
}
